/**
 * Number and address formatting helpers.
 *
 * The UI deals with two shapes of amounts:
 *
 *   - Human strings like "100" or "0.00123". These come from text
 *     inputs and from backend `*Formatted` fields.
 *
 *   - Raw wei as bigint or string. These come from wagmi balance reads
 *     and from backend `amountIn` / `amountOut` fields.
 *
 * The helpers below convert between them safely. We use `formatUnits`
 * and `parseUnits` from viem so the decimals math is correct for tokens
 * at any scale.
 */

import { formatUnits, parseUnits } from "viem";

/**
 * Format a raw-wei bigint as a human-readable decimal string with a
 * sensible precision cap. We never show trailing zeros and we clamp
 * microscopic dust (<1e-6) to 0 so the UI doesn't display noise.
 */
export function formatAmount(
  wei: bigint | string | undefined,
  decimals: number,
  maxFractionDigits = 6,
): string {
  if (wei === undefined) return "0";
  const bi = typeof wei === "bigint" ? wei : BigInt(wei);
  if (bi === 0n) return "0";
  const raw = formatUnits(bi, decimals);
  const [intPart, fracPart = ""] = raw.split(".");
  const clamped = fracPart.slice(0, maxFractionDigits).replace(/0+$/, "");
  return clamped ? `${intPart}.${clamped}` : intPart;
}

/**
 * Format an amount with grouping commas and a specific decimal count.
 * Used by the route breakdown for column alignment.
 */
export function formatAmountWithCommas(
  wei: bigint | string | undefined,
  decimals: number,
  fractionDigits = 4,
): string {
  if (wei === undefined) return "0";
  const bi = typeof wei === "bigint" ? wei : BigInt(wei);
  const raw = formatUnits(bi, decimals);
  const f = Number(raw);
  if (!Number.isFinite(f)) return raw;
  return f.toLocaleString("en-US", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

/**
 * Parse a human decimal string into raw wei bigint. Returns `null` if
 * the input is not a valid decimal number (empty string, letters,
 * multiple dots, etc.), so callers can treat "invalid" uniformly.
 */
export function parseAmount(input: string, decimals: number): bigint | null {
  if (!input || !/^\d*\.?\d*$/.test(input)) return null;
  if (input === "." || input === "") return null;
  try {
    return parseUnits(input, decimals);
  } catch {
    return null;
  }
}

/**
 * Short-form an Ethereum address for wallet button display.
 */
export function shortAddress(addr?: string): string {
  if (!addr) return "";
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

/**
 * Percent renderer with a signed prefix and a fixed number of digits.
 * Used for the uplift pill next to the output amount.
 */
export function formatPct(pct: number, digits = 2): string {
  const sign = pct >= 0 ? "+" : "";
  return `${sign}${pct.toFixed(digits)}%`;
}

/**
 * Debounce helper used by the quote hook so rapid typing collapses to
 * one backend call.
 */
export function debounce<Args extends unknown[]>(
  fn: (...args: Args) => void,
  delayMs: number,
): (...args: Args) => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return (...args: Args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delayMs);
  };
}
